import { initializeOpenAIMeasurement } from './analytics/openai-measurement'
import React, { useEffect } from 'react'
import { Browser } from '@capacitor/browser'
import { App as CapApp } from '@capacitor/app'
import { useAuth0 } from '@auth0/auth0-react'
import { BrowserRouter as Router } from 'react-router-dom'
import { errorLog, infoLog } from './utils/log-helper'
import { useAnalytics, logEvent, timeStart } from './analytics/analytics'
import { captureSessionStart } from './helpers/acquisition'

import RouterApp from './RouterApp'
import { useAppDispatch } from './store'
import { setAccessToken, clearData } from './store/authSlice'
import {
  injectTokenRefresher,
  injectAuthFailureHandler,
  clearLocalStorage,
} from './helpers/auth-helper'
import { useDevice } from './hooks/useDevice'

const App: React.FC = () => {
  // Get the callback handler from the Auth0 React hook
  const {
    handleRedirectCallback,
    getAccessTokenSilently,
    loginWithRedirect,
    isAuthenticated,
    isLoading,
  } = useAuth0()
  const { isHybrid } = useDevice()
  const dispatch = useAppDispatch()

  // Register the Auth0 token refresher so the module-level axios interceptors can silently refresh
  // an expired access token (refresh-token grant on mobile) and retry the request — replacing the
  // old failure mode where a 401 forced the user to close and reopen the app to re-auth.
  useEffect(() => {
    injectTokenRefresher(async (forceRefresh: boolean) => {
      const token = await getAccessTokenSilently(
        forceRefresh ? { cacheMode: 'off' } : undefined,
      )
      dispatch(setAccessToken(token))
      return token
    })
  }, [getAccessTokenSilently, dispatch])

  // Register the dead-token fallback: when the refresh token itself is expired/revoked the silent
  // refresh above can't recover, so the axios interceptor asks us to re-login. This replaces the old
  // "close and reopen the app" workaround. triggerAuthFailure() is debounced, so this fires at most
  // once per expiry window even when a burst of requests 401 at the same time.
  useEffect(() => {
    injectAuthFailureHandler(() => {
      // Loop guards. The debounce in triggerAuthFailure is the primary guard; these back it up:
      //  - isLoading: Auth0 is still initialising / processing a redirect — let it settle.
      //  - !isAuthenticated: the user is already logged out or mid-login (or browsing anon) — don't
      //    hijack them into a login wall; a dead session keeps the SDK's isAuthenticated=true.
      if (isLoading || !isAuthenticated) {
        return
      }
      // Never re-trigger while the auth callback is being processed. On web the callback lands on the
      // origin with ?code&state (mirrors the isAuthCallback check in the appUrlOpen handler below).
      const search = window.location.search
      if (search.includes('code=') && search.includes('state=')) {
        return
      }

      // Drop the stale local auth so the dead token isn't replayed on the next requests.
      clearLocalStorage()
      dispatch(clearData())

      // Preserve where the user was so they land back here after re-auth (matches the app's
      // appState.returnTo convention used at the other login call sites).
      const returnTo = window.location.pathname + window.location.search

      if (isHybrid) {
        // Hybrid opens the system browser and returns via appUrlOpen — same flow as useAuthHelper.
        void loginWithRedirect({
          appState: { returnTo },
          openUrl(url: string) {
            Browser.open({ url, windowName: '_self' }).catch((err) => {
              errorLog('Failed to open browser for re-login:', err)
            })
          },
        })
      } else {
        void loginWithRedirect({ appState: { returnTo } }).catch((err) => {
          errorLog('Re-login redirect failed:', err)
        })
      }
    })
  }, [isLoading, isAuthenticated, isHybrid, loginWithRedirect, dispatch])

  // Initialize analytics
  useAnalytics()
  useEffect(() => initializeOpenAIMeasurement(), [])
  
  // Track app opened once per tab session. sessionStorage (not a ref) so it survives StrictMode's
  // dev double-invoke, HMR, and any remount — app_opened means "app launched", which is once.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('rsly_app_opened') === '1') return
      sessionStorage.setItem('rsly_app_opened', '1')
    } catch {
      // sessionStorage unavailable — fall through and log once for this mount
    }
    timeStart('app_session')
    logEvent('app_opened', {
      screen_name: 'app',
      source: 'in_app',
    })
    // Acquisition capture: parse UTM/gclid/referrer/landing, persist, emit session_started once.
    captureSessionStart()
  }, [])

  useEffect(() => {
    let listener: any = null
    let isHandlingCallback = false

    // Handle the 'appUrlOpen' event and call `handleRedirectCallback`
    const appUrlOpenHandler = async ({
      url,
    }: {
      url: string
    }): Promise<void> => {
      // Prevent multiple simultaneous callbacks
      if (isHandlingCallback) {
        infoLog('Callback already in progress, ignoring duplicate call')
        return
      }

      // Check if this is an authentication callback URL
      const isAuthCallback =
        url.includes('state') && (url.includes('code') || url.includes('error'))

      if (!isAuthCallback) {
        // Not an auth callback, close browser and return
        try {
          await Browser.close()
        } catch (error) {
          errorLog('Error closing browser:', error)
        }
        return
      }

      infoLog('AppUrlOpen URL : ', {url})
      isHandlingCallback = true

      // Check if URL contains an error parameter from Auth0
      let errorParam: string | null = null
      let errorDescription: string | null = null

      try {
        // Try to parse URL - might fail if URL is malformed or missing protocol
        const urlObj = new URL(url)
        errorParam = urlObj.searchParams.get('error')
        errorDescription = urlObj.searchParams.get('error_description')
      } catch (parseError) {
        // If URL parsing fails, try to extract error from URL string directly
        const errorMatch = url.match(/[?&]error=([^&]+)/)
        const errorDescMatch = url.match(/[?&]error_description=([^&]+)/)
        if (errorMatch) {
          errorParam = decodeURIComponent(errorMatch[1])
        }
        if (errorDescMatch) {
          errorDescription = decodeURIComponent(errorDescMatch[1])
        }
      }

      if (errorParam) {
        // Auth0 returned an error (e.g., user denied, access_denied)
        infoLog('Auth0 error in callback:', errorParam, errorDescription)
        // Close browser and let Auth0 SDK handle the error state
        try {
          await Browser.close()
          // Auth0 SDK should handle error state internally
          await handleRedirectCallback(url)
        } catch (error) {
          errorLog('Error handling Auth0 error callback:', error)
          try {
            await Browser.close()
          } catch (closeError) {
            errorLog('Error closing browser after Auth0 error:', closeError)
          }
        } finally {
          isHandlingCallback = false
        }
        return
      }

      try {
        // Handle the redirect callback first (successful auth)
        await handleRedirectCallback(url)
        // Only close browser after successful callback
        // Add a small delay on iPad to ensure callback completes properly
        await new Promise((resolve) => setTimeout(resolve, 150))
        await Browser.close()
      } catch (error) {
        errorLog('Error handling redirect callback:', error)
        // Close browser even on error to prevent stuck state on iPad
        try {
          await new Promise((resolve) => setTimeout(resolve, 100))
          await Browser.close()
        } catch (closeError) {
          errorLog('Error closing browser after error:', closeError)
        }
      } finally {
        isHandlingCallback = false
      }
    }

    // Register the listener
    const registerListener = async (): Promise<void> => {
      try {
        listener = await CapApp.addListener('appUrlOpen', (event) => {
          infoLog('registerListener event: ', {event})
          void appUrlOpenHandler(event)
        })
      } catch (error) {
        errorLog('Error registering appUrlOpen listener:', error)
      }
    }

    // Register the listener
    void registerListener()

    // Cleanup listener on component unmount
    return () => {
      if (listener) {
        try {
          listener.remove().catch((error: any) => {
            errorLog('Error removing listener:', error)
          })
        } catch (error) {
          errorLog('Error removing listener:', error)
        }
      }
    }
  }, [handleRedirectCallback])

  return (
    <Router>
      <RouterApp />
    </Router>
  )
}

export default App
